import { useState, useRef, useCallback, useEffect } from 'react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import type { DailyScrumEntry } from '../../services/dailyScrumService';
import Avatar from '../common/Avatar';
import { useProfileModal } from '../common/ProfileModalProvider';
import type { ProfileColorKey } from '@maios/shared';

interface Props {
  entry: DailyScrumEntry;
  onUpdate: (data: {
    yesterday?: unknown[];
    today?: unknown[];
    blockers?: unknown[];
  }) => void;
  onRemove: () => void;
}

export default function ScrumEntryAccordion({
  entry,
  onUpdate,
  onRemove,
}: Props) {
  const { openProfileModal } = useProfileModal();
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isOpen ? (
            <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
          )}
          <span onClick={(e) => e.stopPropagation()}>
            <Avatar
              size="xs"
              userId={entry.user.id}
              name={entry.user.name}
              avatarUrl={entry.user.avatarUrl}
              displayColor={entry.user.displayColor as ProfileColorKey | null | undefined}
              firstName={entry.user.firstName}
              onClick={() => openProfileModal(entry.user.id)}
            />
          </span>
          <span className="text-sm font-medium text-gray-800">
            {entry.user.name}
          </span>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="p-0.5 text-gray-400 hover:text-red-500 transition-colors"
          title="참여자 제거"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </button>

      {isOpen && (
        <div className="px-3 py-2.5 space-y-3">
          {/* 어제 한 일 */}
          <div>
            <label className="text-[11px] font-medium text-gray-500 mb-1 block">
              어제 한 일
            </label>
            <FreeTextArea
              value={arrayToText((entry.yesterday as string[]) ?? [])}
              onChange={(text) => onUpdate({ yesterday: textToArray(text) })}
              placeholder="어제 한 일을 자유롭게 입력하세요"
            />
          </div>

          {/* 오늘 할 일 */}
          <div>
            <label className="text-[11px] font-medium text-gray-500 mb-1 block">
              오늘 할 일
            </label>
            <FreeTextArea
              value={arrayToText((entry.today as string[]) ?? [])}
              onChange={(text) => onUpdate({ today: textToArray(text) })}
              placeholder="오늘 할 일을 자유롭게 입력하세요"
            />
          </div>

          {/* 이슈 / 장애물 */}
          <div>
            <label className="text-[11px] font-medium text-gray-500 mb-1 block">
              이슈 / 장애물
            </label>
            <FreeTextArea
              value={blockersToText(entry.blockers ?? [])}
              onChange={(text) => onUpdate({ blockers: textToBlockers(text) })}
              placeholder="이슈나 장애물을 자유롭게 입력하세요"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// --- Helpers: array ↔ text 변환 (기존 배열 형식과 호환) ---

function arrayToText(items: string[]): string {
  return items.join('\n');
}

function textToArray(text: string): string[] {
  if (!text.trim()) return [];
  return text.split('\n');
}

function blockersToText(items: { content: string; status: string }[]): string {
  return items.map((i) => i.content).join('\n');
}

function textToBlockers(text: string): { content: string; status: string }[] {
  if (!text.trim()) return [];
  return text.split('\n').map((line) => ({ content: line, status: 'SHARED' }));
}

// --- FreeTextArea (debounced textarea) ---

function FreeTextArea({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (text: string) => void;
  placeholder?: string;
}) {
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const minHeight = 72; // 3줄 기본 높이
    el.style.height = `${Math.max(minHeight, el.scrollHeight)}px`;
  }, []);

  useEffect(() => {
    autoResize();
  }, [autoResize]);

  const handleChange = useCallback(
    (text: string) => {
      autoResize();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => onChange(text), 500);
    },
    [onChange, autoResize],
  );

  return (
    <textarea
      ref={textareaRef}
      defaultValue={value}
      onChange={(e) => handleChange(e.target.value)}
      placeholder={placeholder}
      rows={3}
      className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-2 outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none overflow-hidden bg-transparent"
    />
  );
}
